import Dialog from "@components/Dialog"
import { formatShortcut } from "@features/keymap/keyShortcutFormatter"
import { ShortcutId, shortcuts } from "@features/keymap/shortcuts"
import { MenuItem } from "@mui/material"
import { atom, useAtom, useSetAtom } from "jotai"

const isShortcutsOpenAtom = atom(false)
if (process.env.NODE_ENV !== "production") {
  isShortcutsOpenAtom.debugLabel = "isShortcutsOpenAtom"
}

type ShortcutRow = {
  status: "✅" | "❌"
  shortcut: string
  desc: string
}

type ShortcutSection = ShortcutRow[]

export function ShortcutsDialog() {
  const [isOpen, setIsOpen] = useAtom(isShortcutsOpenAtom)
  const handleClose = () => setIsOpen(false)

  const shortcut = (id: ShortcutId) => formatShortcut(shortcuts[id].shortcut).toUpperCase()
  const desc = (id: ShortcutId) =>
    shortcuts[id].desc.charAt(0).toUpperCase() + shortcuts[id].desc.slice(1)

  return (
    <Dialog closeOnBackdropClick open={isOpen} onClose={handleClose} title="Shortcuts">
      <h2>Builder area:</h2>

      <h3>1. Graph interactions:</h3>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: `${shortcut("pan-graph")} + mouse`,
            desc: desc("pan-graph"),
          },
          {
            status: "✅",
            shortcut: `${shortcut("toggle-select-inside-group")} + mouse`,
            desc: desc("toggle-select-inside-group"),
          },
          {
            status: "✅",
            shortcut: `${formatShortcut("ctrl")} + click`,
            desc: "Select multiple nodes",
          },
          {
            status: "✅",
            shortcut: "DELETE or BACKSPACE",
            desc: "Delete selected node or edge",
          },
          {
            status: "✅",
            shortcut: shortcut("toggle-lock"),
            desc: "Toggle graph lock",
          },
          {
            status: "✅",
            shortcut: `${shortcut("zoom-in")} or ${shortcut("zoom-in-secondary")}`,
            desc: desc("zoom-in"),
          },
          {
            status: "✅",
            shortcut: `${shortcut("zoom-out")} (minus)`,
            desc: desc("zoom-out"),
          },
          {
            status: "✅",
            shortcut: shortcut("fit-view"),
            desc: desc("fit-view"),
          },
          {
            status: "✅",
            shortcut: shortcut("copy-nodes"),
            desc: desc("copy-nodes"),
          },
          {
            status: "✅",
            shortcut: shortcut("paste-nodes"),
            desc: desc("paste-nodes"),
          },
        ]}
      />

      <h3>2. Graph actions:</h3>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: shortcut("toggle-search"),
            desc: desc("toggle-search"),
          },
          {
            status: "✅",
            shortcut: shortcut("delete-graph"),
            desc: desc("delete-graph"),
          },
          {
            status: "✅",
            shortcut: shortcut("share-graph"),
            desc: desc("share-graph"),
          },
          {
            status: "✅",
            shortcut: shortcut("sample-graph"),
            desc: desc("sample-graph"),
          },
          {
            status: "✅",
            shortcut: shortcut("publish-graph"),
            desc: desc("publish-graph"),
          },
          {
            status: "✅",
            shortcut: shortcut("save-snapshot"),
            desc: desc("save-snapshot"),
          },
          {
            status: "✅",
            shortcut: shortcut("restore-snapshot"),
            desc: desc("restore-snapshot"),
          },
          {
            status: "❌",
            shortcut: shortcut("bulk-nodes-edit"),
            desc: desc("bulk-nodes-edit"),
          },
          {
            status: "✅",
            shortcut: shortcut("edit-metadata"),
            desc: desc("edit-metadata"),
          },
          {
            status: "✅",
            shortcut: shortcut("edit-post-sampling-validation"),
            desc: desc("edit-post-sampling-validation"),
          },
          {
            status: "✅",
            shortcut: shortcut("edit-dashboard"),
            desc: desc("edit-dashboard"),
          },
        ]}
      />

      <h3>3. Node drawer actions:</h3>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: shortcut("save-form"),
            desc: desc("save-form"),
          },
          {
            status: "✅",
            shortcut: shortcut("close-drawer"),
            desc: desc("close-drawer"),
          },
        ]}
      />

      <h2>Scenario design area:</h2>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: shortcut("toggle-search"),
            desc: desc("toggle-search"),
          },
          {
            status: "✅",
            shortcut: shortcut("expand-all-groups"),
            desc: desc("expand-all-groups"),
          },
        ]}
      />

      <h2>Results area:</h2>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: shortcut("toggle-search"),
            desc: desc("toggle-search"),
          },
          {
            status: "✅",
            shortcut: shortcut("expand-all-groups"),
            desc: desc("expand-all-groups"),
          },
        ]}
      />

      <h2>Comparison area:</h2>

      <ShortcutsTable
        rows={[
          {
            status: "✅",
            shortcut: shortcut("toggle-search"),
            desc: desc("toggle-search"),
          },
          {
            status: "✅",
            shortcut: shortcut("expand-all-groups"),
            desc: desc("expand-all-groups"),
          },
        ]}
      />
    </Dialog>
  )
}

function ShortcutsTable({ rows }: { rows: ShortcutSection }) {
  return (
    <div>
      <table>
        <thead>
          <tr>
            <th style={{ textAlign: "center" }}>Status</th>
            <th>Shortcut</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td style={{ textAlign: "center" }}>{row.status}</td>
              <td>{row.shortcut}</td>
              <td>{row.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const ShortcutsMenuItem = () => {
  const setIsOpen = useSetAtom(isShortcutsOpenAtom)
  const handleOpen = () => setIsOpen(true)
  return <MenuItem onClick={handleOpen}>Shortcuts</MenuItem>
}
